import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import type { AlertColor } from '@mui/material';
import { toErrorMessage } from '../errors/api-error';

// ── Public API ────────────────────────────────────────────────────────────────
//
// Anywhere in the app:
//
//   const { notify } = useNotification();
//   notify.success('Bien créé');
//   notify.error('Impossible de sauvegarder');             // string
//   notify.error(err);                                    // unknown / Error / Axios error
//   notify.warning('Stock faible', { duration: 8000 });
//   notify.info('Actualisation…');
//
// One Snackbar at a time. New calls replace the current message.
// The provider must wrap the app inside <ThemeProvider>.

export type NotifySeverity = AlertColor;

export interface NotifyOptions {
  /** Auto-hide duration in ms. null disables auto-hide. */
  duration?: number | null;
}

export interface Notify {
  success: (message: string, opts?: NotifyOptions) => void;
  error: (message: unknown, opts?: NotifyOptions) => void;
  warning: (message: string, opts?: NotifyOptions) => void;
  info: (message: string, opts?: NotifyOptions) => void;
}

interface NotificationContextValue {
  notify: Notify;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface SnackbarState {
  open: boolean;
  message: string;
  severity: NotifySeverity;
  duration: number | null;
}

const DEFAULT_DURATION: Record<NotifySeverity, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 6000,
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
    duration: DEFAULT_DURATION.info,
  });

  const show = useCallback((severity: NotifySeverity, message: string, opts?: NotifyOptions) => {
    setState({
      open: true,
      message,
      severity,
      duration: opts?.duration === undefined ? DEFAULT_DURATION[severity] : opts.duration,
    });
  }, []);

  const notify = useMemo<Notify>(
    () => ({
      success: (message, opts) => show('success', message, opts),
      info: (message, opts) => show('info', message, opts),
      warning: (message, opts) => show('warning', message, opts),
      error: (message, opts) => {
        const text = typeof message === 'string' ? message : toErrorMessage(message);
        show('error', text, opts);
      },
    }),
    [show],
  );

  const handleClose = (_event?: unknown, reason?: string) => {
    if (reason === 'clickaway' && state.severity === 'error') return;
    setState((s) => ({ ...s, open: false }));
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={state.duration ?? undefined}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          severity={state.severity}
          variant="filled"
          sx={{ width: '100%', minWidth: 280 }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used inside <NotificationProvider>');
  }
  return ctx;
}
